/**
 * Centralized provider logo configuration
 * Single source of truth for all LLM provider logo paths
 */

export type LLMProviderName = 'gemini' | 'openai' | 'anthropic';

interface ProviderLogoConfig {
  path: string;
  alt: string;
}

const PROVIDER_LOGOS: Record<LLMProviderName, ProviderLogoConfig> = {
  gemini: {
    path: '/gemini-logo.png',
    alt: 'Gemini',
  },
  openai: {
    path: '/openai-logo.png',
    alt: 'OpenAI',
  },
  anthropic: {
    path: '/anthropic-logo.png',
    alt: 'Anthropic',
  },
};

// Default fallback
const DEFAULT_LOGO = PROVIDER_LOGOS.gemini;

/**
 * Get the logo path for a provider
 * @param provider - Provider name (case insensitive)
 * @returns Logo path string
 */
export function getProviderLogoPath(provider: string | undefined | null): string {
  if (!provider) return DEFAULT_LOGO.path;

  const normalized = provider.toLowerCase() as LLMProviderName;
  return PROVIDER_LOGOS[normalized]?.path ?? DEFAULT_LOGO.path;
}

/**
 * Get the alt text for a provider logo
 * @param provider - Provider name (case insensitive)
 * @returns Alt text string
 */
export function getProviderLogoAlt(provider: string | undefined | null): string {
  if (!provider) return DEFAULT_LOGO.alt;

  const normalized = provider.toLowerCase() as LLMProviderName;
  return PROVIDER_LOGOS[normalized]?.alt ?? DEFAULT_LOGO.alt;
}

/**
 * Get full logo config for a provider
 * @param provider - Provider name (case insensitive)
 * @returns Logo config object with path and alt
 */
export function getProviderLogo(provider: string | undefined | null): ProviderLogoConfig {
  if (!provider) return DEFAULT_LOGO;

  const normalized = provider.toLowerCase() as LLMProviderName;
  return PROVIDER_LOGOS[normalized] ?? DEFAULT_LOGO;
}

/**
 * Detect provider from model name string
 * Useful for translation history where we have model names but not provider names
 * @param modelName - Model name string (e.g., "gpt-4", "gemini-2.5-flash", "claude-3")
 * @returns Provider name or null if not detected
 */
export function detectProviderFromModel(modelName: string | undefined | null): LLMProviderName | null {
  if (!modelName) return null;

  const m = modelName.toLowerCase();

  if (m.includes('gemini')) return 'gemini';
  if (m.includes('gpt') || m.includes('openai')) return 'openai';
  if (m.includes('claude') || m.includes('anthropic')) return 'anthropic';

  return null;
}

/**
 * Get logo path from model name (detects provider automatically)
 * @param modelName - Model name string
 * @returns Logo path string
 */
export function getLogoPathFromModel(modelName: string | undefined | null): string {
  const provider = detectProviderFromModel(modelName);
  return getProviderLogoPath(provider);
}

/**
 * Get logo alt from model name (detects provider automatically)
 * @param modelName - Model name string
 * @returns Alt text string
 */
export function getLogoAltFromModel(modelName: string | undefined | null): string {
  const provider = detectProviderFromModel(modelName);
  return getProviderLogoAlt(provider);
}
